import Swal from "sweetalert2";

/**
 * Step quantity dialog (LNS-1405 follow-up)
 *
 * For kg products whose step is a fixed multiple from Karat (category flag "Krok podla hmotnosti produktu"
 * is off), a manually entered quantity that is not a multiple of the step is not applied - the customer
 * picks the nearest lower or higher multiple in a dialog. Products with the flag on keep free input (LNS-1799).
 */

const EPSILON = 1e-6;

// floating point fix for computed multiples (0.8 * 2 = 1.6000000000000001)
const round = (value) => Math.round(value * 1000) / 1000;

/**
 * Returns the step multiples to choose from, or null when no dialog is needed
 * (exact multiple, value below min or above max keep the current behaviour).
 */
export const stepQuantityOptions = (value, step, min, max) => {
    value = parseFloat(value);
    step = parseFloat(step);

    if (isNaN(value) || !(step > 0) || value < min - EPSILON || value > max - EPSILON) {
        return null;
    }

    const ratio = value / step;

    if (Math.abs(ratio - Math.round(ratio)) < EPSILON) {
        return null;
    }

    let lower = round(Math.floor(ratio) * step);
    let upper = round(Math.ceil(ratio) * step);

    if (lower < min - EPSILON) {
        lower = null;
    }

    // higher multiple is not in stock - offer the max instead (stock with tolerance)
    if (upper > max + EPSILON) {
        upper = lower === null || max > lower + EPSILON ? round(max) : null;
    }

    const options = [lower, upper].filter((option) => option !== null);

    return options.length ? options : null;
};

/**
 * Opens the dialog. Resolves to the chosen quantity, or null when closed (quantity stays unchanged).
 * Texts (title, text, entered) come from PHP translations (data-step-texts) or sk.json (Swal.StepQuantity) in Vue.
 */
export const chooseStepQuantity = (options, step, unit, value = null, texts = {}) => {
    if (options.length === 1) {
        return Promise.resolve(options[0]);
    }

    step = parseFloat(step);
    texts = texts || {};

    // 2 -> "2,0 kg", 2.4 -> "2,4 kg", 2.42 -> "2,42 kg"
    const quantityFormat = {
        minimumFractionDigits: 1,
        maximumFractionDigits: 3,
        useGrouping: false,
    };

    let html = (texts.text || "").replace(
        ":step",
        `<strong>${step.toLocaleString("sk", quantityFormat)} ${unit}</strong>`
    );

    if (texts.entered && value !== null && !isNaN(parseFloat(value))) {
        html += `<br>${texts.entered.replace(":value", `${parseFloat(value).toLocaleString("sk", quantityFormat)} ${unit}`)}`;
    }

    const optionButton = (quantity) =>
        `<span class="c-step-quantity__value">${quantity.toLocaleString("sk", quantityFormat)} ${unit}</span>`;

    return Swal.fire({
        title: texts.title || "",
        html: html,
        confirmButtonText: optionButton(options[0]),
        denyButtonText: optionButton(options[1]),
        showDenyButton: true,
        showCloseButton: true,
        buttonsStyling: false,
        focusConfirm: false,
        customClass: {
            popup: "c-step-quantity",
            title: "c-step-quantity__title",
            htmlContainer: "c-step-quantity__text",
            actions: "c-step-quantity__actions",
            confirmButton: "c-step-quantity__option",
            denyButton: "c-step-quantity__option",
            closeButton: "c-step-quantity__close",
        },
    }).then((result) => {
        if (result.isConfirmed) {
            return options[0];
        }

        if (result.isDenied) {
            return options[1];
        }

        return null;
    });
};

// for r-spinner - ui_spinner.js is concatenated by gulp and cannot import
window.Eshop.stepQuantity = {
    options: stepQuantityOptions,
    choose: chooseStepQuantity,
};
